import { createApi } from "@reduxjs/toolkit/query/react";
import { defaultApiConfig } from "./apiSettings";
import { WithPagination } from "./common.types";
import {
  AdminEvent,
  ApplySanctionPayload,
  CreateEventPayload,
  EventModerationOverview,
  ModerationRole,
  PlaceDetails,
  PlaceSuggestion,
  RecommendedAccount,
  SanctionType,
  UpdateEventContentModePayload,
  UpdateEventPayload,
} from "./events.types";
import { defaultQueryStringify } from "libs";

/**
 * Builds a multipart/form-data body for create/update. Keeps the
 * mutation callsite clean — controllers just hand over the shape.
 * The signature is permissive (object literal) on purpose: TS index
 * signatures don't propagate through interface inheritance, so a
 * stricter type here would force every caller to cast.
 */
const toFormData = (payload: object): FormData => {
  const form = new FormData();
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) continue;
    if (value instanceof File) {
      form.append(key, value);
    } else {
      form.append(key, String(value));
    }
  }
  return form;
};

export const eventsApi = createApi({
  ...defaultApiConfig,
  reducerPath: "eventsApi",
  tagTypes: [
    "Events",
    "Event",
    "RecommendedAccounts",
    "Places",
    "EventModeration",
  ],
  endpoints: (builder) => ({
    // --- Events CRUD ---

    listEvents: builder.query<
      WithPagination<AdminEvent>,
      { page?: number; perPage?: number; status?: string }
    >({
      query: (params) => ({
        url: `admin/events?${defaultQueryStringify({
          page: params.page ?? 1,
          perPage: params.perPage ?? 20,
          ...(params.status
            ? { "filters[status][$eq]": params.status }
            : {}),
        })}`,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "Event" as const,
                id,
              })),
              { type: "Events", id: "LIST" },
            ]
          : [{ type: "Events", id: "LIST" }],
    }),

    getEvent: builder.query<AdminEvent, number>({
      query: (id) => ({ url: `admin/events/${id}` }),
      providesTags: (_r, _e, id) => [{ type: "Event", id }],
    }),

    createEvent: builder.mutation<AdminEvent, CreateEventPayload>({
      query: (payload) => ({
        url: "admin/events",
        method: "POST",
        body: toFormData(payload),
      }),
      invalidatesTags: [{ type: "Events", id: "LIST" }],
    }),

    /**
     * Update — uses POST (Laravel form spoofing). FormRequest accepts
     * any subset of fields; nulls mean "leave unchanged".
     */
    updateEvent: builder.mutation<AdminEvent, UpdateEventPayload>({
      query: ({ id, ...payload }) => ({
        url: `admin/events/${id}`,
        method: "POST",
        body: toFormData(payload),
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Event", id },
        { type: "Events", id: "LIST" },
      ],
    }),

    /**
     * The only event setting writable once the event is live. Separate
     * endpoint, JSON body — no multipart, nothing else travels with it,
     * so it can never half-apply another field.
     */
    updateEventContentMode: builder.mutation<
      AdminEvent,
      UpdateEventContentModePayload
    >({
      query: ({ id, contentMode }) => ({
        url: `admin/events/${id}/content-mode`,
        method: "PATCH",
        body: { contentMode },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Event", id },
        { type: "Events", id: "LIST" },
      ],
    }),

    endEvent: builder.mutation<AdminEvent, number>({
      query: (id) => ({
        url: `admin/events/${id}/end`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: "Event", id },
        { type: "Events", id: "LIST" },
      ],
    }),

    // --- Google Maps proxy ---

    placesAutocomplete: builder.query<
      PlaceSuggestion[],
      { q: string; sessionToken: string; country?: string }
    >({
      query: ({ q, sessionToken, country }) => ({
        url: `admin/maps/places/autocomplete?${defaultQueryStringify({
          q,
          sessionToken,
          country,
        })}`,
      }),
      transformResponse: (resp: { data: PlaceSuggestion[] }) => resp.data,
    }),

    placeDetails: builder.query<
      PlaceDetails,
      { placeId: string; sessionToken: string }
    >({
      query: ({ placeId, sessionToken }) => ({
        url: `admin/maps/places/${placeId}?${defaultQueryStringify({
          sessionToken,
        })}`,
      }),
      transformResponse: (resp: { data: PlaceDetails }) => resp.data,
    }),

    // --- Recommended accounts (#12b) ---

    listRecommendedAccounts: builder.query<RecommendedAccount[], void>({
      query: () => ({ url: "admin/recommended-accounts" }),
      transformResponse: (resp: { data: RecommendedAccount[] }) => resp.data,
      providesTags: [{ type: "RecommendedAccounts", id: "LIST" }],
    }),

    setRecommendedAccounts: builder.mutation<
      RecommendedAccount[],
      { userIds: number[] }
    >({
      query: (body) => ({
        url: "admin/recommended-accounts",
        method: "POST",
        body,
      }),
      transformResponse: (resp: { data: RecommendedAccount[] }) => resp.data,
      invalidatesTags: [{ type: "RecommendedAccounts", id: "LIST" }],
    }),

    // --- Moderation ---
    //
    // Moderation routes are mirrored under both `/public/` (mobile
    // moderators, gated by PublicMiddleware which requires the
    // `role:user` token ability) and `/admin/` (sporadmin, gated by
    // AdminMiddleware which requires `admin`/`superadmin`). Sporadmin
    // bearers can't pass PublicMiddleware, so we MUST use the admin
    // mirror — otherwise every moderation request returns 401 and
    // baseQueryWithReauth boots the session to /login.

    getEventModeration: builder.query<EventModerationOverview, number>({
      query: (id) => ({ url: `admin/events/${id}/moderation` }),
      transformResponse: (resp: { data: EventModerationOverview }) => resp.data,
      providesTags: (_r, _e, id) => [{ type: "EventModeration", id }],
    }),

    addEventModerator: builder.mutation<
      void,
      { eventId: number; userId: number; role: ModerationRole }
    >({
      query: ({ eventId, userId, role }) => ({
        url: `admin/events/${eventId}/moderators`,
        method: "POST",
        body: { userId, role },
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: "EventModeration", id: eventId },
      ],
    }),

    removeEventModerator: builder.mutation<
      void,
      { eventId: number; userId: number }
    >({
      query: ({ eventId, userId }) => ({
        url: `admin/events/${eventId}/moderators/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: "EventModeration", id: eventId },
      ],
    }),

    applyEventSanction: builder.mutation<
      void,
      ApplySanctionPayload & { eventId: number }
    >({
      query: ({ eventId, ...body }) => ({
        url: `admin/events/${eventId}/sanctions`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: "EventModeration", id: eventId },
      ],
    }),

    liftEventSanction: builder.mutation<
      void,
      { eventId: number; userId: number; type: SanctionType }
    >({
      query: ({ eventId, userId, type }) => ({
        url: `admin/events/${eventId}/sanctions/${userId}/${type}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: "EventModeration", id: eventId },
      ],
    }),
  }),
});

export const {
  useListEventsQuery,
  useGetEventQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useUpdateEventContentModeMutation,
  useEndEventMutation,
  useLazyPlacesAutocompleteQuery,
  useLazyPlaceDetailsQuery,
  useListRecommendedAccountsQuery,
  useSetRecommendedAccountsMutation,
  useGetEventModerationQuery,
  useAddEventModeratorMutation,
  useRemoveEventModeratorMutation,
  useApplyEventSanctionMutation,
  useLiftEventSanctionMutation,
} = eventsApi;
